/**
 * ScriptDetectionDebugger - A component for debugging script detection in real-time
 *
 * This component displays detailed logs from the TieredSimilarityScriptDetector,
 * showing similarity checks, thresholds, decisions, and reasoning in real-time.
 */
import { useCallback, useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ScriptDetectionLogPayload } from "@frontierx/shared/schemas/clientMessageTypes";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Search,
  Target,
  Trash2,
  XCircle,
  Zap,
} from "lucide-react";

interface ScriptDetectionDebuggerProps {
  logs: ScriptDetectionLogPayload[];
  onClearLogs: () => void;
}

type DecisionFilter =
  | "all"
  | "completed"
  | "ai_check_recommended"
  | "no_match"
  | "skipped"
  | "ai_completed";
type MethodFilter =
  | "all"
  | "local_keyword"
  | "keyword"
  | "cohere_paraphrase"
  | "openai_embedding";

const DECISION_COLORS = {
  completed: "bg-green-100 text-green-800 border-green-200",
  ai_check_recommended: "bg-yellow-100 text-yellow-800 border-yellow-200",
  no_match: "bg-gray-100 text-gray-800 border-gray-200",
  skipped: "bg-blue-100 text-blue-800 border-blue-200",
  ai_completed: "bg-purple-100 text-purple-800 border-purple-200",
} as const;

const DECISION_ICONS = {
  completed: CheckCircle,
  ai_check_recommended: AlertCircle,
  no_match: XCircle,
  skipped: Clock,
  ai_completed: Zap,
} as const;

const METHOD_COLORS = {
  local_keyword: "bg-blue-50 text-blue-700",
  keyword: "bg-indigo-50 text-indigo-700",
  cohere_paraphrase: "bg-green-50 text-green-700",
  openai_embedding: "bg-purple-50 text-purple-700",
} as const;

export function ScriptDetectionDebugger({
  logs,
  onClearLogs,
}: Readonly<ScriptDetectionDebuggerProps>) {
  const [searchTerm, setSearchTerm] = useState("");
  const [decisionFilter, setDecisionFilter] = useState<DecisionFilter>("all");
  const [methodFilter, setMethodFilter] = useState<MethodFilter>("all");
  const [autoScroll, setAutoScroll] = useState(true);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logs.length > 0) {
      const element = document.getElementById("script-detection-logs-end");
      element?.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs, autoScroll]);

  // Filter logs based on search and filters
  const filteredLogs = logs.filter((log) => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        log.scriptItemText.toLowerCase().includes(searchLower) ||
        log.triggeringText.toLowerCase().includes(searchLower) ||
        log.reason.toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;
    }

    // Decision filter
    if (decisionFilter !== "all" && log.decision !== decisionFilter) {
      return false;
    }

    // Method filter
    if (methodFilter !== "all") {
      const hasMethod = log.checksPerformed.some(
        (check) => check.method === methodFilter,
      );
      if (!hasMethod) return false;
    }

    return true;
  });

  const formatTimestamp = useCallback((timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  }, []);

  const formatScore = useCallback((score: number) => {
    return `${(score * 100).toFixed(1)}%`;
  }, []);

  const getScoreColor = useCallback(
    (score: number, partialThreshold: number, fullThreshold: number) => {
      if (score >= fullThreshold) return "text-green-600 font-semibold";
      if (score >= partialThreshold) return "text-yellow-600 font-medium";
      return "text-gray-500";
    },
    [],
  );

  return (
    <Card className="h-full">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          Script Detection Debugger
          <Badge variant="secondary">{logs.length} logs</Badge>
        </CardTitle>

        {/* Controls */}
        <div className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search script text, trigger text, or reasoning..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          <Select
            value={decisionFilter}
            onValueChange={(value) =>
              setDecisionFilter(value as DecisionFilter)
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by decision" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Decisions</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="ai_check_recommended">
                AI Check Recommended
              </SelectItem>
              <SelectItem value="no_match">No Match</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
              <SelectItem value="ai_completed">AI Completed</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={methodFilter}
            onValueChange={(value) => setMethodFilter(value as MethodFilter)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              <SelectItem value="local_keyword">Local Keyword</SelectItem>
              <SelectItem value="keyword">Keyword</SelectItem>
              <SelectItem value="cohere_paraphrase">
                Cohere Paraphrase
              </SelectItem>
              <SelectItem value="openai_embedding">OpenAI Embedding</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoScroll(!autoScroll)}
          >
            Auto-scroll: {autoScroll ? "On" : "Off"}
          </Button>

          <Button
            variant="destructive"
            size="sm"
            onClick={onClearLogs}
            className="flex items-center gap-1"
          >
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="max-h-[600px] overflow-y-auto space-y-2 p-4">
          {filteredLogs.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {logs.length === 0
                ? "No script detection logs yet. Start a call to see activity."
                : "No logs match your current filters."}
            </div>
          )}

          {filteredLogs.map((log, index) => {
            const DecisionIcon = DECISION_ICONS[log.decision];

            return (
              <Card
                key={`log-${log.timestamp}-${index}`}
                className="border-l-4 border-l-blue-500"
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <DecisionIcon className="h-4 w-4" />
                      <Badge className={DECISION_COLORS[log.decision]}>
                        {log.decision.replace("_", " ").toUpperCase()}
                      </Badge>
                      <span className="text-xs text-gray-500 font-mono">
                        {formatTimestamp(log.timestamp)}
                      </span>
                    </div>
                  </div>

                  <Tabs defaultValue="overview" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="overview">Overview</TabsTrigger>
                      <TabsTrigger value="checks">
                        Checks ({log.checksPerformed.length})
                      </TabsTrigger>
                      <TabsTrigger value="details">Details</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="space-y-3">
                      <div>
                        <span className="text-sm font-medium text-gray-600">
                          Script Item:
                        </span>
                        <p className="text-sm bg-blue-50 p-2 rounded mt-1">
                          {log.scriptItemText}
                        </p>
                      </div>

                      <div>
                        <span className="text-sm font-medium text-gray-600">
                          Triggering Text:
                        </span>
                        <p className="text-sm bg-yellow-50 p-2 rounded mt-1">
                          {log.triggeringText}
                        </p>
                      </div>

                      <div>
                        <span className="text-sm font-medium text-gray-600">
                          Reasoning:
                        </span>
                        <p className="text-sm bg-gray-50 p-2 rounded mt-1">
                          {log.reason}
                        </p>
                      </div>
                    </TabsContent>

                    <TabsContent value="checks" className="space-y-2">
                      {log.checksPerformed.map((check, checkIndex) => (
                        <div
                          key={`check-${checkIndex}`}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded border"
                        >
                          <div className="flex items-center gap-2">
                            <Badge className={METHOD_COLORS[check.method]}>
                              {check.method.replace("_", " ")}
                            </Badge>
                            <span
                              className={`font-mono text-sm ${getScoreColor(
                                check.score,
                                check.partialThreshold,
                                check.fullThreshold,
                              )}`}
                            >
                              {formatScore(check.score)}
                            </span>
                          </div>

                          <div className="text-xs text-gray-500">
                            Thresholds: {formatScore(check.partialThreshold)} /{" "}
                            {formatScore(check.fullThreshold)}
                          </div>
                        </div>
                      ))}
                    </TabsContent>

                    <TabsContent value="details" className="space-y-2">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-600">
                            Script Item ID:
                          </span>
                          <p className="font-mono text-xs bg-gray-100 p-1 rounded mt-1">
                            {log.scriptItemId}
                          </p>
                        </div>

                        <div>
                          <span className="font-medium text-gray-600">
                            Decision:
                          </span>
                          <p className="mt-1">
                            <Badge className={DECISION_COLORS[log.decision]}>
                              {log.decision}
                            </Badge>
                          </p>
                        </div>
                      </div>

                      <div>
                        <span className="text-sm font-medium text-gray-600">
                          Methods Attempted:
                        </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {log.checksPerformed.map((check, checkIndex) => (
                            <Badge
                              key={`method-${checkIndex}`}
                              variant="outline"
                              className={METHOD_COLORS[check.method]}
                            >
                              {check.method}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            );
          })}

          <div id="script-detection-logs-end" />
        </div>
      </CardContent>
    </Card>
  );
}
